import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { useRouter } from "next/router"
import { F_Q_STORE_INFO } from "lib/function"

const HELLO_BAR_STORAGE_PREFIX = "nagarey-hello-bar-dismissed:"

function hashHelloContent(html) {
  let h = 0
  if (!html) return "0"
  for (let i = 0; i < html.length; i++) {
    h = (Math.imul(31, h) + html.charCodeAt(i)) | 0
  }
  return String(h)
}

const BASE_MARQUEE_DURATION = 5

const HelloBar = () => {
  const { locale } = useRouter()
  const [helloHtml, setHelloHtml] = useState(null)
  const [dismissed, setDismissed] = useState(false)
  const rootRef = useRef(null)
  const marqueeRef = useRef(null)
  const segRef = useRef(null)
  const [marqueeConfig, setMarqueeConfig] = useState({
    count: 2,
    duration: BASE_MARQUEE_DURATION,
  })

  const dismissStorageKey = useMemo(() => {
    if (!helloHtml) return null
    return `${HELLO_BAR_STORAGE_PREFIX}${hashHelloContent(helloHtml)}`
  }, [helloHtml])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const response = await F_Q_STORE_INFO(
        { lang: locale },
        process.env.INIT_TOKEN
      )
      const raw = response?.data?.storeInfo?.helloBar
      const text = typeof raw === "string" ? raw.trim() : ""
      if (!cancelled) setHelloHtml(text || null)
    })()
    return () => {
      cancelled = true
    }
  }, [locale])

  useLayoutEffect(() => {
    if (!dismissStorageKey || typeof window === "undefined") return
    setDismissed(sessionStorage.getItem(dismissStorageKey) === "1")
  }, [dismissStorageKey])

  useEffect(() => {
    if (!helloHtml || dismissed) return
    const root = rootRef.current?.querySelector(".hello-bar__marquee")
    if (!root) return
    root.querySelectorAll("a[href]").forEach((a) => {
      a.setAttribute("target", "_blank")
      a.setAttribute("rel", "noopener noreferrer")
    })
  }, [helloHtml, dismissed])

  useLayoutEffect(() => {
    if (!helloHtml || dismissed) return
    const container = marqueeRef.current
    const seg = segRef.current
    if (!container || !seg) return

    const measure = () => {
      const containerW = container.offsetWidth
      const segW = seg.offsetWidth
      if (segW === 0) return
      const perHalf = Math.max(1, Math.ceil(containerW / segW))
      setMarqueeConfig({
        count: perHalf * 2,
        duration: BASE_MARQUEE_DURATION * perHalf,
      })
    }

    measure()
    window.addEventListener("resize", measure)
    return () => window.removeEventListener("resize", measure)
  }, [helloHtml, dismissed])

  const handleDismiss = useCallback(() => {
    if (dismissStorageKey) {
      sessionStorage.setItem(dismissStorageKey, "1")
    }
    setDismissed(true)
  }, [dismissStorageKey])

  useLayoutEffect(() => {
    const clear = () => {
      document.documentElement.style.setProperty("--hello-bar-height", "0px")
    }

    if (!helloHtml || dismissed) {
      clear()
      return
    }

    const el = rootRef.current
    if (!el) {
      clear()
      return
    }

    const apply = () => {
      document.documentElement.style.setProperty(
        "--hello-bar-height",
        `${el.offsetHeight}px`
      )
    }

    apply()

    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(apply)
        : null
    ro?.observe(el)
    window.addEventListener("resize", apply)

    return () => {
      clear()
      ro?.disconnect()
      window.removeEventListener("resize", apply)
    }
  }, [helloHtml, dismissed])

  if (!helloHtml || dismissed) return null

  const closeLabel =
    locale === "id" ? "Tutup pengumuman" : "Close announcement"

  return (
    <div className="hello-bar" ref={rootRef}>
      <div className="hello-bar__body">
        <div className="hello-bar__marquee" ref={marqueeRef}>
          <div
            className="hello-bar__marquee-track"
            style={{ animationDuration: `${marqueeConfig.duration}s` }}
          >
            {Array.from({ length: marqueeConfig.count }, (_, i) => (
              <div
                key={i}
                ref={i === 0 ? segRef : undefined}
                className="hello-bar__marquee-seg"
                dangerouslySetInnerHTML={{ __html: helloHtml }}
                aria-hidden={i > 0 || undefined}
              />
            ))}
          </div>
        </div>
      </div>
      <button
        type="button"
        className="hello-bar__close"
        onClick={handleDismiss}
        aria-label={closeLabel}
      >
        <span className="air ai-times" aria-hidden />
      </button>
    </div>
  )
}

export default HelloBar
